import React, { useState, useEffect } from 'react'
import { Modal, Input, Button, message } from 'antd'
import { showSuccessToast } from 'src/utils/messageToast'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { requestNewSupplier } from 'src/features/SupplierSlice'

const InviteSupplierModal = ({
  visible,
  onCancel,
  supplierName = '',
  onSuccess,
}) => {
  const dispatch = useDispatch()
  const venueId = useSelector(state => state.venue?.venue?._id)
  const [formData, setFormData] = useState({
    supplierName: supplierName || '',
    supplierEmail: '',
    accountNumber: '',
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (visible) {
      setFormData({
        supplierName: supplierName || '',
        supplierEmail: '',
        accountNumber: '',
      })
      setErrors({})
    }
  }, [visible, supplierName])

  const handleCancel = () => {
    setFormData({
      supplierName: supplierName || '',
      supplierEmail: '',
      accountNumber: '',
    })
    setErrors({})
    onCancel()
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.supplierName.trim()) {
      newErrors.supplierName = 'Supplier name is required'
    }

    if (!formData.supplierEmail.trim()) {
      newErrors.supplierEmail = 'Supplier email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supplierEmail)) {
      newErrors.supplierEmail = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    if (!venueId) {
      message.error('Missing required information')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await dispatch(
        requestNewSupplier(
          venueId,
          formData.supplierName.trim(),
          formData.supplierEmail.trim(),
          formData.accountNumber.trim() || '',
          '',
          true,
          {
            toastMessage:
              'Supplier invite sent! We will add them to your account in a few days. We will send you an email when they have been added.',
          },
        ),
      )

      if (response?.success) {
        showSuccessToast(
          response.message ||
            'Supplier invite sent! We will add them to your account in a few days. We will send you an email when they have been added.',
        )
        handleCancel()
        onSuccess?.()
      } else {
        message.error(response?.message || 'Unable to send supplier invite')
      }
    } catch (error) {
      console.error(error)
      message.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <Modal
      title='Invite new supplier'
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <ModalContent>
        <Description>
          We&apos;ll email the supplier and ask them to join OMS so you can
          start ordering from them. Thanks for helping grow the OMS network —
          every supplier you add makes ordering easier for everyone.
        </Description>

        <InputWrapper>
          <Label>Supplier Name</Label>
          <Input
            value={formData.supplierName}
            onChange={e => handleChange('supplierName', e.target.value)}
            placeholder='Enter supplier name'
            size='large'
            status={errors.supplierName ? 'error' : ''}
          />
          {errors.supplierName && <ErrorText>{errors.supplierName}</ErrorText>}
        </InputWrapper>

        <InputWrapper>
          <Label>Supplier Email</Label>
          <Input
            type='email'
            value={formData.supplierEmail}
            onChange={e => handleChange('supplierEmail', e.target.value)}
            placeholder='johnsmith@gmail.com'
            size='large'
            status={errors.supplierEmail ? 'error' : ''}
          />
          {errors.supplierEmail && (
            <ErrorText>{errors.supplierEmail}</ErrorText>
          )}
        </InputWrapper>

        <InputWrapper>
          <Label>Account Number (optional)</Label>
          <Input
            value={formData.accountNumber}
            onChange={e => handleChange('accountNumber', e.target.value)}
            placeholder='Enter account number'
            size='large'
          />
        </InputWrapper>

        <ButtonContainer>
          <Button onClick={handleCancel} size='large' disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={handleSubmit}
            size='large'
            loading={isSubmitting}
          >
            Send
          </Button>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  )
}

const ModalContent = styled.div`
  padding: 20px 0;
`

const Description = styled.p`
  font-size: 14px;
  color: #595959;
  line-height: 1.6;
  margin-bottom: 24px;
  text-align: center;
`

const InputWrapper = styled.div`
  margin-bottom: 24px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #262626;
`

const ErrorText = styled.span`
  display: block;
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

export default InviteSupplierModal
